import { Button } from "$components/button/button";
import { toast } from "$global-providers/toast-provider";
import RHFDynamicFormFields from "$routes/_lib/rhf-dynamic-form-fields/rhf-dynamic-form-fields";
import ValidationContext from "$routes/_lib/validation/validation-context";
import {
  L10N,
  translateI18nObject,
} from "$routes/patient/[:patientId]/_lib/translation-util";
import { gql } from "@apollo/client";
import { convertOrderedSchema, parseSchema } from "@elephant-healthcare/yupify";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box } from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import {
  type InvestigationActivity,
  type VisitInvestigationActivity,
} from "elephant-web/gql";
import { clone, omit } from "ramda";
import React from "react";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import { useIntl } from "react-intl";
import {
  useCompleteInvestigationsMutation,
  useInvestigationCardCurrentVisitQuery,
} from "./__generated__/investigation-activity-card-form.gql";
import {
  InvestigationActivitiesProvider,
  useInvestigationActivitiesContext,
} from "./investigation-activities-provider";
import { useInvestigationsCache } from "./use-investigations-cache";

gql`
  ${InvestigationActivitiesProvider.fragments.investigationActivity}
  mutation completeInvestigations($input: [CompleteInvestigationInput]!) {
    completeInvestigations(input: $input) {
      ...InvestigationActivityFragment
    }
  }
`;

interface Props {
  investigation_id: string;
  investigationRequest:
    | InvestigationActivity["request"]
    | VisitInvestigationActivity["request"];
  disabled?: boolean;
  paymentBlocked?: boolean;
  patientUuid: string;

  /**
   * This prop indicates if the patient has been admitted during their current visit.
   * For example, as an inpatient.
   */
  isOngoingAdmissionVisit: boolean;
}

const SubmitButton = ({
  onSubmit,
  loading,
  paymentBlocked,
  ...delegateProps
}) => {
  const { formState } = useFormContext();
  return (
    <Button
      // See comment below about nested forms and why this can't be a submit button.
      // type="submit"
      data-testid="investigation-submit-button"
      loading={loading}
      // Must disable, because we can't use that browser form API submission validation,
      // because of the below comment about nested forms.
      disabled={!formState.isValid || paymentBlocked}
      onClick={onSubmit}
      {...delegateProps}
    >
      {"Submit"}
    </Button>
  );
};

const InvestigationActivityCardForm = ({
  investigation_id,
  investigationRequest,
  isOngoingAdmissionVisit,
  disabled = false,
  paymentBlocked = false,
  patientUuid,
}: Props): React.ReactElement => {
  const { formatMessage } = useIntl();

  const translatedRequest = translateI18nObject(investigationRequest, L10N.EN);
  const orderedSchema = omit(["meta"], translatedRequest);

  const { validationSchema, defaultValues } = React.useMemo(() => {
    const formSchema = convertOrderedSchema(orderedSchema);
    const clonedSchema = clone(formSchema);
    const validationSchema = parseSchema(clonedSchema);
    return { validationSchema, defaultValues: validationSchema.getDefault() };
  }, [orderedSchema]);

  const { refetch: refetchInvestigationsHistory } =
    useInvestigationActivitiesContext();
  const { appendInvestigationsToCache } = useInvestigationsCache(patientUuid);
  const [completeInvestigations, { loading: submitLoading }] =
    useCompleteInvestigationsMutation({
      onCompleted: (data) => {
        appendInvestigationsToCache(data.completeInvestigations, true);
        refetchInvestigationsHistory();
        toast.success(
          formatMessage({
            id: "InvestigationActivityCardForm.completeInvestigation.success",
            defaultMessage: "Investigation completed",
          }),
        );
      },
      onError: (error) => {
        toast.error(
          formatMessage(
            {
              id: "InvestigationActivityCardForm.completeInvestigation.error",
              defaultMessage:
                "There was an error completing the investigation: {error}",
            },
            { error: error.message },
          ),
        );
      },
    });

  const formMethods = useForm({
    mode: "onTouched",
    resolver: yupResolver(validationSchema),
    defaultValues,
  });

  return (
    <ValidationContext.Provider value={{ validationSchema }}>
      <FormProvider {...formMethods}>
        <form
          name="investigation"
          autoComplete="off"
          /**
           * NOPE - this form is often nested within other forms, example ANC, (naughty naughty)
           * so let's handle this submission programmatically to avoid unintended behavior from nested forms
           */
          // onSubmit={}
        >
          <RHFDynamicFormFields schema={orderedSchema} disabled={disabled} />
          {disabled ? null : (
            <SubmitButton
              style={{ display: "block", marginLeft: "auto" }}
              loading={submitLoading}
              paymentBlocked={paymentBlocked}
              onSubmit={formMethods.handleSubmit((values) =>
                completeInvestigations({
                  variables: {
                    input: [
                      {
                        id: investigation_id,
                        outcome: values,
                        isInpatient: isOngoingAdmissionVisit,
                      },
                    ],
                  },
                }),
              )}
            />
          )}
        </form>
      </FormProvider>
    </ValidationContext.Provider>
  );
};

export const InvestigationCardPlaceholder = () => {
  return (
    <Box>
      <Skeleton variant="text" height={40} />
      <Skeleton variant="text" height={40} />
      <Skeleton variant="text" height={40} />
    </Box>
  );
};

export const InvestigationCardCurrentVisitDocument = gql`
  query investigationCardCurrentVisit($uuid: String!, $searchWholeTerritory: Boolean) {
    patientByCardId(uuid: $uuid, searchWholeTerritory: $searchWholeTerritory) {
      passportId # requesting this prevents collisions with other requests to the patientByCardId resolver
      patient {
        currentVisit {
          admissionEvent {
            _id
            type
          }
        }
      }
    }
  }
`;

/**
 *
 * @returns a wrapped InvestigationActivityCardForm component that provides
 * information of the patient's current visit type
 */
export const InvestigationCardFormForVisit = (
  props: Omit<Props, "isOngoingAdmissionVisit">,
) => {
  const { patientUuid } = props;

  // we wish to know if this patient is currently an inpatient as to inform
  // downstream consumers of our data. Therefore, let's query for the visit information
  // This query should almost always hit the cache as we're mounting this on the patient history
  // view
  const { data, loading } = useInvestigationCardCurrentVisitQuery({
    variables: {
      uuid: patientUuid,
      searchWholeTerritory: true,
    },
    skip: !patientUuid,
  });
  const currentVisit = data?.patientByCardId?.patient?.currentVisit;
  const isOngoingAdmissionVisit = !!currentVisit?.admissionEvent;

  if (loading) {
    return <InvestigationCardPlaceholder />;
  }

  return (
    <InvestigationActivityCardForm
      isOngoingAdmissionVisit={isOngoingAdmissionVisit}
      {...props}
    />
  );
};

export default InvestigationActivityCardForm;
